import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { CreatePartnerRequest, Contact, PartnerType, BankAccount } from '../../models/partner';
import { PartnersService } from '../../../services/partners.service';
import { AttachmentService } from '../../../services/attachment.service';
import { Attachment } from '../../models/attachment';

@Component({
  selector: 'app-add-partner',
  templateUrl: './add-partner.component.html',
  styleUrls: ['./add-partner.component.scss']
})
export class AddPartnerComponent {
  partner: any = {
    contact: {
      name: '',
      email: '',
      phoneNumber: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: ''
      }
    },
    type: PartnerType.DENTIST,
    faxNo: '',
    bankAccounts: []
  };

  loading = false;
  error: string | null = null;
  success = false;
  logoAttachment: { id?: string; fileName?: string; extension?: string; url?: string; } | undefined = undefined;
  isUploadingLogo = false;

  partnerTypes = Object.values(PartnerType).map(type => ({
    value: type,
    label: type
  }));

  constructor(
    private partnersService: PartnersService,
    private attachmentService: AttachmentService,
    private router: Router
  ) {}

  onSubmit(): void {
    if (!this.partner.contact.name?.trim()) {
      this.error = 'Partner name is required';
      return;
    }

    if (!this.partner.contact.email?.trim()) {
      this.error = 'Email is required';
      return;
    }

    if (!this.partner.contact.phoneNumber?.trim()) {
      this.error = 'Phone number is required';
      return;
    }

    this.loading = true;
    this.error = null;

    // Create the partner data according to the swagger specification
    const partnerData: CreatePartnerRequest = {
      contact: {
        name: this.partner.contact.name.trim(),
        phoneNumber: this.partner.contact.phoneNumber.trim(),
        email: this.partner.contact.email.trim(),
        address: {
          street: this.partner.contact.address.street.trim(),
          city: this.partner.contact.address.city.trim(),
          state: this.partner.contact.address.state.trim(),
          zip: this.partner.contact.address.zip.trim()
        }
      },
      type: this.partner.type,
      faxNo: this.partner.faxNo?.trim() || undefined,
      bankAccounts: this.partner.bankAccounts && this.partner.bankAccounts.length > 0 
        ? this.partner.bankAccounts.filter((ba: BankAccount) => ba.name?.trim() || ba.number?.trim())
        : undefined,
      logoAttachment: this.logoAttachment ? {
        id: this.logoAttachment.id || '',
        fileName: this.logoAttachment.fileName || ''
      } : undefined
    };

    this.partnersService.createPartner(partnerData).subscribe({
      next: (createdPartner) => {
        console.log('Partner created successfully:', createdPartner);
        this.success = true;
        this.loading = false;
        
        // Redirect to partners list after 2 seconds
        setTimeout(() => {
          this.router.navigate(['/partners']);
        }, 2000);
      },
      error: (error) => {
        console.error('Error creating partner:', error);
        this.error = error.error?.message || 'Failed to create partner. Please try again.';
        this.loading = false;
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/partners']);
  }

  resetForm(): void {
    this.partner = {
      contact: {
        name: '',
        email: '',
        phoneNumber: '',
        address: {
          street: '',
          city: '',
          state: '',
          zip: ''
        }
      },
      type: PartnerType.DENTIST,
      faxNo: '',
      bankAccounts: []
    };
    this.logoAttachment = undefined;
    this.error = null;
    this.success = false;
  }

  addBankAccount(): void {
    if (!this.partner.bankAccounts) {
      this.partner.bankAccounts = [];
    }
    this.partner.bankAccounts.push({ name: '', number: '' });
  }

  removeBankAccount(index: number): void {
    if (this.partner.bankAccounts && index >= 0 && index < this.partner.bankAccounts.length) {
      this.partner.bankAccounts.splice(index, 1);
    }
  }

  onLogoFileSelected(file: File): void {
    this.isUploadingLogo = true;
    this.error = null;

    this.attachmentService.uploadFileWithProgress(file).subscribe({
      next: (result) => {
        if (result.progress === 100 && result.attachmentId) {
          // Create a minimal attachment object with just the ID for compatibility
          this.logoAttachment = { 
            id: result.attachmentId.toString(),
            fileName: 'uploaded-logo.jpg' // Default filename
          };
          this.isUploadingLogo = false;
          console.log('Logo uploaded successfully. Attachment ID:', result.attachmentId);
        }
      },
      error: (error) => {
        console.error('Error uploading logo:', error);
        this.error = 'Failed to upload logo. Please try again.';
        this.isUploadingLogo = false;
      }
    });
  }

  onLogoRemoved(): void {
    this.logoAttachment = undefined;
  }
}